import {
  ActionType,
  MeetingStatus,
  Severity,
  useMeetingStatus,
  useNotificationDispatch,
} from "amazon-chime-sdk-component-library-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { logVideoRoomError } from "../utils/logVideoRoomError";

type DemoMeetingStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "failed";

// Cap reconnection notifications
const MAX_RECONNECT_NOTIFICATIONS = 3;

const MeetingStatusNotifier: React.FC = () => {
  const meetingStatus = useMeetingStatus();
  const dispatch = useNotificationDispatch();
  const [status, setStatus] = useState<DemoMeetingStatus>();
  const [statusMessage, setStatusMessage] = useState("");
  const reconnectCountRef = useRef(0);
  const navigate = useNavigate();
  const { meetingId } = useParams();

  const getMeetingStatusPayload = (message: string, severity: Severity) => {
    return {
      severity,
      message,
      autoClose: true,
      replaceAll: true,
    };
  };

  useEffect(() => {
    switch (meetingStatus) {
      case MeetingStatus.Loading:
        setStatus("connecting");
        setStatusMessage("Meeting connecting...");
        break;
      case MeetingStatus.Succeeded:
        setStatus("connected");
        reconnectCountRef.current = 0;
        if (status === "reconnecting") {
          setStatusMessage("Meeting reconnected.");
          dispatch({
            type: ActionType.ADD,
            payload: getMeetingStatusPayload(
              "Meeting reconnected",
              Severity.SUCCESS
            ),
          });
        } else {
          setStatusMessage("Meeting connected.");
          dispatch({
            type: ActionType.ADD,
            payload: getMeetingStatusPayload(
              "Meeting connected",
              Severity.SUCCESS
            ),
          });
        }
        break;
      case MeetingStatus.Reconnecting:
        setStatus("reconnecting");
        setStatusMessage("Meeting reconnecting...");
        dispatch({
          type: ActionType.ADD,
          payload: getMeetingStatusPayload(
            "Meeting reconnecting...",
            Severity.WARNING
          ),
        });
        break;
      case MeetingStatus.Failed:
        setStatus("failed");
        setStatusMessage("Meeting failed. Redirecting...");
        dispatch({
          type: ActionType.ADD,
          payload: getMeetingStatusPayload(
            "Meeting failed even after reconnection attempts, redirecting to home",
            Severity.ERROR
          ),
        });
        logVideoRoomError(
          "Meeting failed after reconnection attempts",
          "meeting_failed",
          { meetingId }
        );
        navigate(meetingId ? `/video/end/${meetingId}` : "/dashboard");
        break;
      case MeetingStatus.TerminalFailure:
        setStatus("failed");
        setStatusMessage("Meeting failed. Redirecting...");
        dispatch({
          type: ActionType.ADD,
          payload: getMeetingStatusPayload(
            "Meeting will not reconnect due to fatal failure, redirecting to home",
            Severity.ERROR
          ),
        });
        logVideoRoomError(
          "Meeting terminal failure",
          "meeting_terminal_failure",
          { meetingId }
        );
        navigate(meetingId ? `/video/end/${meetingId}` : "/dashboard");
        break;
      default:
        break;
    }
    return () => {
      setStatus(undefined);
    };
  }, [meetingStatus]);

  // Cap reconnection notifications at MAX_RECONNECT_NOTIFICATIONS,
  // then switch to a single persistent (non-autoclose) notification.
  useEffect(() => {
    let id: ReturnType<typeof setInterval>;
    if (status === "reconnecting") {
      id = setInterval(() => {
        reconnectCountRef.current += 1;

        if (reconnectCountRef.current <= MAX_RECONNECT_NOTIFICATIONS) {
          dispatch({
            type: ActionType.ADD,
            payload: getMeetingStatusPayload(
              "Meeting reconnecting...",
              Severity.WARNING
            ),
          });
        } else if (reconnectCountRef.current === MAX_RECONNECT_NOTIFICATIONS + 1) {
          // Switch to persistent notification
          dispatch({
            type: ActionType.ADD,
            payload: {
              severity: Severity.WARNING,
              message: "Still trying to reconnect...",
              autoClose: false,
              replaceAll: true,
            },
          });
          clearInterval(id);
        }
      }, 10 * 1000);
    }
    return () => {
      clearInterval(id);
    };
  }, [status]);

  // Visually-hidden aria-live region announces meeting status to screen readers
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {statusMessage}
    </div>
  );
};

export default MeetingStatusNotifier;
